import { useDispatch } from 'react-redux';
import { useCallback, useState, useMemo } from 'react';
import { Formik, FormikHelpers } from 'formik';
import useSWR, { KeyedMutator } from 'swr';
import { Button, Stack } from '@mui/material';

import { ContactManagerLayout } from '@modules/ContactManager/components/Tabs/shared/ContactManagerLayout';
import { ContactErrorMessage } from '@modules/ContactManager/components/constants';
import { SaveButton } from '@shared/components/SaveButton';
import { incomeTypes } from '@shared/constants/lists';
import { ContactTabHeader } from '@styles/ContactManager/TabsStyles';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';

import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { queryParams } from '@shared/commonFunctions';
import { FormWithPrompt } from '@shared/components/FormWithPrompt';
import { useRefreshComments } from '@shared/hooks/useRefreshComments';
import { useTabs } from '@shared/hooks/useTabs';
import { UnableToSaveModal } from '../../shared/UnableToSaveModal';
import { OCRModal } from './components/OCRModal/OCRModal';
import { CoApplicantModal } from './components/CoApplicant/CoApplicant';
import { ICoApplicant } from './components/CoApplicant/validation';
import { ContactForm } from './ContactForm';
import { requestUpdateContact} from './queries';
import { ContactModel, contactValidationSchema, NContact } from './validation';
import { OptOutModal } from './components/OptOutModal/OptOutModal';
import { ViewAdditionalInfoModal } from './components/ViewAdditionalInfoModal';
import { IContactDetails } from './components/OptOutModal/interfaces';
interface ContactPropsType {
  contactData: NContact.Entity;
  paramId: string;
  displayAdditionalInfo: boolean;
  reloadContact: KeyedMutator<NContact.Entity>;
}

export const Contact = ({ contactData, paramId, displayAdditionalInfo, reloadContact }: ContactPropsType) => {
  const params = queryParams();
  const dispatch = useDispatch();
  const { updateCurrentTab } = useTabs();
  const mutateComments = useRefreshComments(false, params?.id, false);

  const [coApplicantIdToEdit, setCoApplicantIdToEdit] = useState<NContact.CoApplicant['id'] | null>(null);
  const [coApplicantModalPosition, setCoApplicantModalPosition] = useState(0);
  const [isOCRModalOpen, setOCRModalOpen] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [optOutModalIsOpen, setOptOutModalIsOpen] = useState(0);
  const [isAdditionalInfoModalOpen, setIsAdditionalInfoModalOpen] = useState(false);

  const { data: ocrData, isValidating, mutate: mutateOcrData } = useSWR(`document-service/ocrresult/${paramId}`);
  
  const contactDetails :IContactDetails ={
    firstName:contactData?.firstName??'',
    lastName:contactData?.lastName??'',
    email:contactData?.email??'',
    cellPhone:contactData?.cellPhone??''
   };

  const initialState = useMemo<NContact.Entity>(
    () => ({
      ...contactData,
      addresses: (contactData?.addresses || []).map((address) => ({
        ...address,
        streetLine1: address.streetLine1?.slice(0, 26) || '',
      })),
      incomes: (contactData?.employments || []).map((employment) => ({
        ...employment,
        position: employment.jobTitle?.slice(0, 26) || '',
        companyName: employment.companyName?.slice(0, 26) || '',
      })),
      otherIncomes: (contactData?.otherIncomes || []).map((income) => ({
        ...income,
        incomeType: incomeTypes.find((type) => type.label.replace(/\s+/g, '') === income.incomeType)?.value || incomeTypes[0].value,
      })),
    }),
    [contactData, incomeTypes]
  );

  const handleSubmit = useCallback(
    async (submitData: NContact.Entity, formikHelpers: FormikHelpers<NContact.Entity>) => {
      setLoading(true);
      const hasNoPhoneNumber = !submitData.cellPhone && !submitData.workPhone && !submitData.homePhone;

      if (hasNoPhoneNumber) {
        setLoading(false);
        formikHelpers.setErrors({
          cellPhone: `Cell Phone: ${ContactErrorMessage.NO_PHONE_ERROR_MESSAGE}`,
          homePhone: `Home Phone: ${ContactErrorMessage.NO_PHONE_ERROR_MESSAGE}`,
          workPhone: `Work Phone: ${ContactErrorMessage.NO_PHONE_ERROR_MESSAGE}`
        });
        return;
      }

      const data = new ContactModel(submitData);
      const sortedAddress = data.addresses?.sort((a, b) => Number(b.isCurrent) - Number(a.isCurrent)) || [];
      const isMLAChanged =
        initialState?.isMilitaryLendingActAvailable === submitData?.isMilitaryLendingActAvailable ? false : true;
      const isOneCreditPullChanged =
        initialState?.oneCreditPull === submitData?.oneCreditPull ? false : true;
      const isEmailMatching = data.coApplicants.some(coApplicant => coApplicant.email === data.email);
      const isCellPhoneMatching = data.coApplicants.some(coApplicant => coApplicant.cellPhone === data.cellPhone);
      if (contactData.email != data.email && contactData.cellPhone != data.cellPhone && isEmailMatching && isCellPhoneMatching) {
        formikHelpers.setErrors({
          email: ContactErrorMessage.EMAIL_MATCHING_ERROR_MESSAGE,
          cellPhone: ContactErrorMessage.CELL_PHONE_MATCHING_ERROR_MESSAGE
        });
      }
      else {
        if (contactData.email != data.email && isEmailMatching) {
          formikHelpers.setErrors({ email: ContactErrorMessage.EMAIL_MATCHING_ERROR_MESSAGE });
        }
        else if (contactData.cellPhone != data.cellPhone && isCellPhoneMatching) {
          formikHelpers.setErrors({ cellPhone: ContactErrorMessage.CELL_PHONE_MATCHING_ERROR_MESSAGE });
        }
        else {
          const response = await requestUpdateContact({
            ...data,
            addresses: sortedAddress,
            isMLAFlagValueChanged: isMLAChanged,
            isOneCreditPullValueChanged: isOneCreditPullChanged,
          });

          if (isMLAChanged) {
            mutateComments();
          }

          if (isOneCreditPullChanged) {
            mutateComments();
          }
          
        //If email is changed added 1 sec delay after updating email
        if (contactData?.email !== data?.email) {
           await new Promise((resolve) => setTimeout(resolve, 1000));
          }

          if (!response?.error) {
            await reloadContact();
            formikHelpers.resetForm();
            updateCurrentTab({
              title: `${submitData.firstName} ${submitData.lastName}`,
            });
          }

          formikHelpers.setSubmitting(false);
          setLoading(false);
          dispatch(setLastCommentsRefresh(new Date().toISOString()));
          return response;
        }
      }
      setLoading(false);
    },
    [requestUpdateContact, initialState]
  );

  const handleCoApplicantClick = useCallback(() => {
    setCoApplicantIdToEdit('new');
    setCoApplicantModalPosition((prevState) => prevState + 1);
  }, []);

  const handleCloseCoApplicantModal = useCallback(() => {
    setCoApplicantIdToEdit(null);
    setCoApplicantModalPosition(0);
  }, []);

  return (
    <ContactManagerLayout isLoading={!contactData}>
      {coApplicantIdToEdit && (
        <CoApplicantModal
          open
          title="Co-Applicant"
          contactId={paramId}
          contactInfo={contactData}
          coApplicantModalPosition={coApplicantModalPosition}
          coApplicantIdToEdit={coApplicantIdToEdit}
          addresses={initialState?.addresses || undefined}
          reloadContact={reloadContact}
          onClose={handleCloseCoApplicantModal}
        />
      )}

      <ContactTabHeader>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 1 }}
        >
          <Button
            disabled={!(initialState as ICoApplicant)?.coApplicantId && !paramId}
            variant="contained"
            size="small"
            onClick={() => setOptOutModalIsOpen((prevState) => prevState + 1)}
          >
            Consent
          </Button>
        </Stack>

        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 1 }}
        >
          {((initialState as ICoApplicant)?.coApplicantId || initialState.id) && displayAdditionalInfo && (
            <Button
              type="button"
              variant="contained"
              color="success"
              size="small"
              onClick={() => setIsAdditionalInfoModalOpen(true)}
            >
              View Additional Info
            </Button>
          )}

          <Button
            variant="contained"
            size="small"
            color="info"
            onClick={() => setOCRModalOpen((prevState) => prevState + 1)}
          >
            Data Review
          </Button>

          <Button
            variant="contained"
            size="small"
            color="info"
            onClick={handleCoApplicantClick}
          >
            Add Co-Applicant
          </Button>
        </Stack>

        {!!optOutModalIsOpen && (
          <OptOutModal
            optOutModalPosition={optOutModalIsOpen}
            handleClose={() => setOptOutModalIsOpen(0)}
            contactDetails={contactDetails}
          />
        )}

        {isAdditionalInfoModalOpen && (
          <ViewAdditionalInfoModal
            additionalInfo={initialState?.additionalInfo || {}}
            handleClose={() => setIsAdditionalInfoModalOpen(false)}
          />
        )}
      </ContactTabHeader>

      <LoaderBackdrop open={isLoading} />

      <Formik
        enableReinitialize
        initialValues={initialState}
        validationSchema={contactValidationSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <FormWithPrompt>
            <>
              <SaveButton
                disabled={!props.dirty}
                isLoading={props.isSubmitting}
                top="-110px"
              />

              <ContactForm
                contactId={paramId}
                textBtnColor="warning"
                coApplicants={contactData?.coApplicants}
                setCoApplicantModalPosition={setCoApplicantModalPosition}
                onEditCoApplicant={setCoApplicantIdToEdit}
                onSave={handleSubmit}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...(props as any)}
              />

              <UnableToSaveModal />

              {!!isOCRModalOpen && (
                <OCRModal
                  ocrModalPosition={isOCRModalOpen}
                  ocrData={ocrData}
                  loading={isValidating}
                  mutateOcrData={mutateOcrData}
                  handleClose={() => setOCRModalOpen(0)}
                />
              )}
            </>
          </FormWithPrompt>
        )}
      </Formik>
    </ContactManagerLayout>
  );
};
